import { AuthState, getResourceHeadersWithTokenAtom, Keys, LogoutReason, UnknownAppStage } from '@eih-netbroker/common';
import { useAuth as useOIDCAuth, User } from 'oidc-react';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Config } from '../shared/config/config';
import { routes } from '../shared/data/routes';
import { isOidcAuthError } from '../shared/services/authorization/oidcErrors';
import { useAuthorizationFlow } from '../shared/services/authorization/useAuthorizationFlow';
import { useAxiosInterceptorsEffect } from '../shared/services/authorization/useAxiosInterceptorsEffect';
import { useLogout } from '../shared/services/authorization/useLogout';
import { useUserInactivity } from '../shared/services/authorization/useUserInactivity';
import { useResourceHeaders } from '../shared/services/useResource';
import { store } from '../shared/state/store';
import { useLogger } from '../shared/utils/useLogger';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const logger = useLogger(`AuthProvider`);
  const [debuggerVisible, setDebuggerVisible] = useState<boolean>(
    window.localStorage.getItem(Keys.DebuggerVisible) === 'true',
  );
  const [debuggerComponent, setDebuggerComponent] = useState<ReactNode>();
  const { getResourceHeaderWithToken } = useResourceHeaders();
  const { appAuth, loginInternal, initiateLogout, loadNextAppStage, loadSSEManager } = useAuthorizationFlow();
  const { isLoading, userData, signOut, signOutRedirect, userManager } = useOIDCAuth();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();

  const tokenRefreshPromiseRef = useRef<Promise<User | null> | null>(null);

  useUserInactivity();

  const initAppDebugger = useCallback(() => {
    window.addEventListener('keydown', e => {
      if ((e.code === 'KeyD' && e.ctrlKey) || e.code === 'F2') {
        setDebuggerVisible(true);
        window.localStorage.setItem(Keys.DebuggerVisible, 'true');
      }
    });
  }, [setDebuggerVisible]);

  /**
   * Silent token refresh with error classification.
   *
   * - Resolves with the new `User` when SSO returns a fresh token.
   * - Resolves with `null` for transient failures (network error, SSO unreachable, timeout).
   *   The caller should fall back to the current token — if it really is expired the server
   *   will answer 401 and the response interceptor takes over.
   * - Rejects (throws) when the refresh token itself is no longer usable (invalid_grant etc.).
   *   The caller is expected to log the user out.
   *
   * The in-flight promise is deduplicated and ALWAYS cleared in `finally` so a failed refresh
   * does not leave a rejected promise cached for subsequent requests.
   */
  const refreshToken = useCallback(async (): Promise<User | null> => {
    if (tokenRefreshPromiseRef.current) {
      return tokenRefreshPromiseRef.current;
    }

    const promise = (async () => {
      try {
        return await userManager.signinSilent();
      } catch (err) {
        if (isOidcAuthError(err)) {
          logger.error('Silent token refresh failed — refresh token no longer usable', err);
          throw err;
        }
        logger.warn('Silent token refresh failed (transient/network) — keeping current token', err);
        return null;
      } finally {
        tokenRefreshPromiseRef.current = null;
      }
    })();

    tokenRefreshPromiseRef.current = promise;
    return promise;
  }, [userManager, logger]);

  useAxiosInterceptorsEffect({ logout, refreshToken, userManager });

  /**
   * Handle silent renew and refresh token
   */
  useEffect(() => {
    const onUserLoaded = (updatedUser: User | null) => {
      // this should only be called when the user is already authenticated, so we can refresh the token
      if (!appAuth.isAuthenticated) {
        return;
      }
      if (updatedUser) {
        logger.info(`User refreshed by oauth - ${updatedUser.profile?.name}`);
        loginInternal(updatedUser.access_token, updatedUser.id_token || '', { refreshToken: true });
      } else {
        logger.error(`User is null after oauth refresh`);
        initiateLogout(signOut, LogoutReason.SSOAuthError);
      }
    };
    userManager.events.addUserLoaded(onUserLoaded);

    userManager.events.addSilentRenewError(err => {
      logger.error(`Silent renew error - ${err.message}`, err);
    });
    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
    };
  }, [userManager, appAuth, loginInternal, initiateLogout, signOut, logger]);

  // for debugging purposes
  // useEffect(() => {
  //   console.log(`AuthState: ${appAuth.authStatus} - ${(appAuth as any).stage || 'N/A'}`);
  // }, [appAuth]);

  /**
   * Enable the debugger
   */
  useEffect(() => {
    if (Config.App.EnableAppDebugger) {
      initAppDebugger();
    }
  }, [initAppDebugger]);

  /**
   * Authorization flow which loads the initial data to start the application
   */
  useEffect(() => {
    (async () => {
      if (appAuth.isAuthenticated && appAuth.authStatus === AuthState.Loading) {
        if (location.pathname === routes.login || location.pathname === routes.authorize) {
          navigate(routes.home, { replace: true });
        }

        loadNextAppStage();
      }
    })();
  }, [appAuth, loadNextAppStage, location, navigate]);

  /**
   * Logout is triggered through the appAuth state
   */
  useEffect(() => {
    if (appAuth.authStatus === AuthState.SignOff && appAuth.error === LogoutReason.SSOCallbackError) {
      // if we are in a popup window or in an iframe, we need to send a message to the parent window to close the popup
      userManager.signinCallback(undefined);
    } else if (appAuth.authStatus === AuthState.SignOff) {
      // if token is expired we can clean up the local OIDC state
      if (appAuth.error === LogoutReason.TokenExpired) {
        logger.debug(`Token expired, removing user`);
        userManager.removeUser();
      } else if (appAuth.error === LogoutReason.SignInFailedToInitializeApp) {
        logger.error(`Sign in failed to initialize app, removing user`);
        userManager.removeUser();
      }
      initiateLogout(() => signOutRedirect({ post_logout_redirect_uri: `${window.location.origin}/` }), appAuth.error);
    }
  }, [appAuth, initiateLogout, signOutRedirect, userManager, logger]);

  /**
   * Authorization initialization
   * When OIDC client is loaded we check for any stored user and start the login process
   */
  useEffect(() => {
    if (!isLoading && appAuth.authStatus === AuthState.Unknown && appAuth.stage === UnknownAppStage.SSOLoading) {
      if (location.pathname === routes.authorize && (window.opener || window.parent)) {
        // if we are in a popup window or in an iframe, we need to send a message to the parent window to close the popup
        userManager.signinCallback(window.location.search);

        // nothing to do here, since this instance of NetBroker is either a popup or an iframe
        return;
      }

      // no userdata available, but we are in the authorize page, we can just wait until the user is redirected back or handle errors
      if (!userData && location.pathname === routes.authorize) {
        if (location.search.includes('error')) {
          logger.info(`SSO login error: ${location.search}`);
          // if there is an error during SSO login, local logout is enough
          initiateLogout(signOut, LogoutReason.SSOAuthError);
          return;
        }
        // when we are on authorize page, OIDC will handle sign in, don't do anything here
        // Do nothing
      } else if (userData) {
        // if we have userData we can log in
        loginInternal(userData.access_token, userData.id_token || '');
      } else {
        // no userData is available, need to init SSE Manager to find out if there is a session in another tab
        loadSSEManager();
      }
    }
  }, [
    isLoading,
    appAuth,
    location,
    navigate,
    loginInternal,
    userData,
    loadSSEManager,
    initiateLogout,
    signOut,
    userManager,
    logger,
  ]);

  /**
   * Toggle AppDebugger
   */
  useEffect(() => {
    if (debuggerVisible && !debuggerComponent) {
      logger.info(`Opening AppDebugger`);
      import('../shared/components/app-debug/AppDebug').then(({ AppDebug }) => {
        setDebuggerComponent(
          <AppDebug
            visible={debuggerVisible}
            hideDebugger={() => {
              setDebuggerVisible(false);
              window.localStorage.removeItem(Keys.DebuggerVisible);
            }}
          />,
        );
      });
    }
  }, [debuggerVisible, debuggerComponent, logger]);

  /**
   * Update getResourceHeaderWithToken function reference so non react components can use it
   */
  useEffect(() => {
    store.set(getResourceHeadersWithTokenAtom, {
      fn: () => getResourceHeaderWithToken,
    });
  }, [getResourceHeaderWithToken, appAuth]);

  return (
    <>
      {children}
      {debuggerVisible && <React.Suspense fallback={<div />}>{debuggerComponent && debuggerComponent}</React.Suspense>}
    </>
  );
};
